import PipelineLostReasonsAPI from '../../api/pipelineLostReasons';

// Catálogo de motivos de pérdida (P10). `records` tiene lo último que se pidió:
// solo activos (tablero, ficha, conversación) o también los inactivos
// (Configuración → Pipelines, administradores).
const errorMessage = error =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message;

const wrap = error => {
  const wrapped = new Error(errorMessage(error));
  wrapped.code = error?.response?.data?.code;
  return wrapped;
};

export const state = {
  records: [],
  suggestions: [],
  includesInactive: false,
  uiFlags: { isFetching: false, isFetched: false, fetchError: false },
};

export const getters = {
  getReasons: $state => $state.records,
  getActiveReasons: $state => $state.records.filter(reason => reason.active),
  getSuggestions: $state => $state.suggestions,
  getUIFlags: $state => $state.uiFlags,
};

export const actions = {
  async get({ commit }, { includeInactive = false } = {}) {
    commit('SET_UI_FLAG', { isFetching: true, fetchError: false });
    try {
      const { data } = await PipelineLostReasonsAPI.get({ includeInactive });
      commit('SET_CATALOG', { ...data, includesInactive: includeInactive });
      commit('SET_UI_FLAG', { isFetched: true });
      return data.payload;
    } catch (error) {
      commit('SET_UI_FLAG', { fetchError: true });
      throw wrap(error);
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },
  // El tablero y la ficha piden una vez por sesión; Configuración siempre recarga.
  async ensureLoaded({ state: $state, dispatch }) {
    if ($state.uiFlags.isFetched) return $state.records;
    return dispatch('get');
  },
  async create({ commit, state: $state }, names) {
    try {
      const { data } = await PipelineLostReasonsAPI.createMany(names, {
        includeInactive: $state.includesInactive,
      });
      commit('SET_CATALOG', {
        ...data,
        includesInactive: $state.includesInactive,
      });
      return data.payload;
    } catch (error) {
      throw wrap(error);
    }
  },
  async update({ commit }, { id, ...changes }) {
    try {
      const { data } = await PipelineLostReasonsAPI.update(id, changes);
      commit('UPDATE_REASON', data);
      return data;
    } catch (error) {
      throw wrap(error);
    }
  },
  async delete({ commit }, id) {
    try {
      await PipelineLostReasonsAPI.delete(id);
      commit('DELETE_REASON', id);
    } catch (error) {
      throw wrap(error);
    }
  },
  async reorder({ commit, state: $state }, ids) {
    try {
      const { data } = await PipelineLostReasonsAPI.reorder(ids, {
        includeInactive: $state.includesInactive,
      });
      commit('SET_CATALOG', {
        ...data,
        includesInactive: $state.includesInactive,
      });
      return data.payload;
    } catch (error) {
      throw wrap(error);
    }
  },
};

export const mutations = {
  SET_UI_FLAG($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
  SET_CATALOG($state, { payload, suggestions, includesInactive }) {
    $state.records = payload || [];
    $state.suggestions = suggestions || [];
    $state.includesInactive = Boolean(includesInactive);
  },
  UPDATE_REASON($state, reason) {
    $state.records = $state.records.map(record =>
      record.id === reason.id ? { ...record, ...reason } : record
    );
  },
  DELETE_REASON($state, id) {
    $state.records = $state.records.filter(record => record.id !== id);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
